import { useEffect, useState } from 'react';

import { API } from '../../API';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { getWorkspaceId, getWorkspaceRoles } from '../../utils/artifactsUi';
import { EmbeddingMode, WindmillApp } from '../../pages/WindmillAppPage/types';

const WINDMILL_ROLE = 'menu:windmill_apps';

// One request per workspace + placement for the whole page: a message list
// renders a menu per card, and every card asks for the same list.
const cache = new Map<string, Promise<WindmillApp[]>>();

function fetchEmbeddedApps(
  workspaceId: number | undefined,
  mode: EmbeddingMode,
): Promise<WindmillApp[]> {
  const key = `${workspaceId}:${mode}`;
  let pending = cache.get(key);
  if (!pending) {
    pending = API.fetch(
      'GET',
      `/API/v2/windmill/apps?embedding_mode=${mode}`,
    ).catch(() => {
      // Drop the failure so a later mount can retry; the menu just stays empty.
      cache.delete(key);
      return [];
    });
    cache.set(key, pending as Promise<WindmillApp[]>);
  }
  return pending as Promise<WindmillApp[]>;
}

/**
 * Windmill apps the current workspace has embedded in `mode`. Empty (and no
 * request made) when `enabled` is false or the workspace lacks the Windmill
 * Apps role — the backend would reject the call anyway.
 */
export function useEmbeddedWindmillApps(
  mode: EmbeddingMode,
  enabled = true,
): WindmillApp[] {
  const [currentUser] = useCurrentUser();
  const allowed =
    enabled && Boolean(getWorkspaceRoles(currentUser)?.includes(WINDMILL_ROLE));
  const workspaceId = getWorkspaceId(currentUser);
  const [apps, setApps] = useState<WindmillApp[]>([]);

  useEffect(() => {
    if (!allowed) {
      setApps([]);
      return;
    }
    let active = true;
    fetchEmbeddedApps(workspaceId, mode).then((result) => {
      if (active) setApps(result);
    });
    return () => {
      active = false;
    };
  }, [allowed, workspaceId, mode]);

  return apps;
}
